import { z } from 'zod';
import { Slug } from './common.js';
import { Role } from './roles.js';
import { FeaturePreset } from './features.js';
/**
 * Passwordless sign-in (brief: Identity and access). The email carries a link and a 6-digit
 * code. Either only works in the browser that asked for it (bound by an HttpOnly nonce cookie);
 * on another device the person types the code into the original browser.
 */
export const RequestMagicLinkInput = z.object({ email: z.email().max(254) });
export const RequestMagicLinkResponse = z.object({
    flowId: z.uuid(),
    /** Native apps only (no cookies): the device binding the app keeps and sends back with verify. */
    nonce: z.string().optional(),
});
/** Native apps send the nonce they were given; browsers rely on the HttpOnly nonce cookie. */
const NativeNonce = { nonce: z.string().min(20).max(200).optional() };
export const VerifyMagicLinkInput = z.union([
    z.object({ token: z.string().min(20).max(200), ...NativeNonce }),
    z.object({ flowId: z.uuid(), code: z.string().regex(/^\d{6}$/), ...NativeNonce }),
]);
/**
 * Native apps send `x-chapsi-client: native` and no cookies; they get tokens in the body instead and
 * keep them in the OS keychain (brief: Identity and access). Never returned to a browser.
 */
export const NATIVE_CLIENT_HEADER = 'x-chapsi-client';
export const NativeTokens = z.object({
    /** Bearer token for API calls and the WebSocket; null when the person has no workspace yet. */
    accessToken: z.string().nullable(),
    accessExpiresInSeconds: z.number().int(),
    /** Sent as `x-chapsi-refresh` to /v1/auth/refresh; rotated on every refresh. Absent when unchanged. */
    refreshToken: z.string().optional(),
});
export const WorkspaceSummary = z.object({
    id: z.uuid(),
    name: z.string(),
    slug: Slug,
    role: Role,
});
/**
 * - `none`: the person has no second factor set up, so none is asked for.
 * - `required`: they have one (a passkey or an authenticator) and this device has not proved it yet.
 * - `verified`: this device proved it.
 * `setupRequired`: the current workspace requires two-factor sign-in and the person has none yet.
 */
export const SecondFactorState = z.object({
    status: z.enum(['none', 'required', 'verified']),
    methods: z.object({ passkey: z.boolean(), totp: z.boolean(), recoveryCodes: z.boolean() }),
    setupRequired: z.boolean(),
});
export const SessionState = z.object({
    user: z.object({
        id: z.uuid(),
        name: z.string(),
        email: z.email(),
        /** Whether the person has at least one passkey, so the web can offer one after a magic-link sign-in. */
        hasPasskey: z.boolean(),
    }),
    workspaces: z.array(WorkspaceSummary),
    /** The workspace the access cookie is currently scoped to, if any. */
    currentWorkspaceId: z.uuid().nullable(),
    /**
     * Two-factor sign-in (docs/adr/0033-two-factor-and-security-alerts.md). While `status` is `required` or
     * `setupRequired` is true there is no access token: the client shows the second-factor or setup screen.
     */
    secondFactor: SecondFactorState,
    /**
     * The person asked to delete their account (docs/adr/0040-account-deletion.md). Until `scheduledAt` there is
     * no access token and no workspace: the client shows the scheduled-deletion screen with Cancel deletion.
     */
    accountDeletion: z.object({ scheduledAt: z.string() }).nullable(),
    /**
     * The current workspace requires single sign-on for this person and this device did not sign in through its
     * identity provider (docs/adr/0055). There is no access token: the client offers "Continue with single sign-on".
     */
    ssoRequired: z.object({ workspaceId: z.uuid(), workspaceName: z.string() }).nullable().optional(),
    /** Native apps only. */
    native: NativeTokens.optional(),
});
export const SwitchWorkspaceInput = z.object({ workspaceId: z.uuid() });
export const CreateWorkspaceInput = z.object({
    name: z.string().trim().min(2).max(80),
    slug: Slug,
    preset: FeaturePreset.optional(),
});
export const UpdateProfileInput = z.object({ name: z.string().trim().min(1).max(80) });
export const DeviceSession = z.object({
    id: z.uuid(),
    userAgent: z.string().nullable(),
    /** "Chrome on macOS", from the User-Agent. */
    label: z.string(),
    /** The network the device signed in from: the first three IPv4 octets ("203.0.113.x") or the IPv6 /48. */
    network: z.string().nullable(),
    /** Approximate location when the CDN reported one at sign-in ("Sydney, AU"). */
    location: z.string().nullable(),
    createdAt: z.string(),
    lastUsedAt: z.string(),
    current: z.boolean(),
    /** Whether this device proved a second factor (only meaningful when the person has one). */
    secondFactorVerified: z.boolean(),
});
export const DeviceSessionList = z.object({ items: z.array(DeviceSession) });
/**
 * Passkeys (docs/adr/0026-passkeys.md). WebAuthn options and responses travel as the standard JSON
 * shapes (what @simplewebauthn/browser produces and consumes). The fields the server relies on are
 * validated; anything else the browser adds passes through to the WebAuthn library, which checks it.
 */
const B64Url = z.string().regex(/^[A-Za-z0-9_-]*$/, 'base64url').max(20_000);
const CredentialDescriptor = z.looseObject({
    id: B64Url,
    type: z.literal('public-key'),
    transports: z.array(z.string().max(30)).max(10).optional(),
});
/** PublicKeyCredentialCreationOptionsJSON, for navigator.credentials.create(). */
export const PasskeyCreationOptions = z.looseObject({
    challenge: B64Url,
    rp: z.looseObject({ name: z.string(), id: z.string().optional() }),
    user: z.looseObject({ id: B64Url, name: z.string(), displayName: z.string() }),
    pubKeyCredParams: z.array(z.looseObject({ type: z.literal('public-key'), alg: z.number().int() })),
    timeout: z.number().int().optional(),
    excludeCredentials: z.array(CredentialDescriptor).optional(),
    authenticatorSelection: z
        .looseObject({
        residentKey: z.enum(['discouraged', 'preferred', 'required']).optional(),
        requireResidentKey: z.boolean().optional(),
        userVerification: z.enum(['discouraged', 'preferred', 'required']).optional(),
    })
        .optional(),
    attestation: z.enum(['none', 'indirect', 'direct', 'enterprise']).optional(),
});
/** PublicKeyCredentialRequestOptionsJSON, for navigator.credentials.get(). */
export const PasskeyRequestOptions = z.looseObject({
    challenge: B64Url,
    timeout: z.number().int().optional(),
    rpId: z.string().optional(),
    allowCredentials: z.array(CredentialDescriptor).optional(),
    userVerification: z.enum(['discouraged', 'preferred', 'required']).optional(),
});
/** RegistrationResponseJSON from navigator.credentials.create(). */
export const PasskeyRegistrationResponse = z.looseObject({
    id: B64Url.min(1),
    rawId: B64Url.min(1),
    type: z.literal('public-key'),
    response: z.looseObject({
        clientDataJSON: B64Url.min(1),
        attestationObject: B64Url.min(1),
        transports: z.array(z.string().max(30)).max(10).optional(),
    }),
    clientExtensionResults: z.record(z.string(), z.unknown()).default({}),
    authenticatorAttachment: z.enum(['platform', 'cross-platform']).optional(),
});
/** AuthenticationResponseJSON from navigator.credentials.get(). */
export const PasskeyAuthenticationResponse = z.looseObject({
    id: B64Url.min(1),
    rawId: B64Url.min(1),
    type: z.literal('public-key'),
    response: z.looseObject({
        clientDataJSON: B64Url.min(1),
        authenticatorData: B64Url.min(1),
        signature: B64Url.min(1),
        userHandle: B64Url.optional(),
    }),
    clientExtensionResults: z.record(z.string(), z.unknown()).default({}),
    authenticatorAttachment: z.enum(['platform', 'cross-platform']).optional(),
});
export const PasskeyName = z.string().trim().min(1).max(60);
export const Passkey = z.object({
    id: z.uuid(),
    name: z.string(),
    createdAt: z.string(),
    lastUsedAt: z.string().nullable(),
    /** Synced to the person's cloud keychain (iCloud, Google Password Manager…), so it survives a lost device. */
    backedUp: z.boolean(),
    deviceType: z.enum(['singleDevice', 'multiDevice']),
    /**
     * Set when the passkey's signature counter went backwards (a possible copy of a security key). It no
     * longer signs in; remove it and add it again.
     */
    flaggedAt: z.string().nullable(),
});
export const PasskeyList = z.object({ items: z.array(Passkey) });
/**
 * Asking to add a passkey needs a fresh second factor: a session that signed in within the last 10 minutes,
 * or `code`, the six-digit code emailed by `requestPasskeyAddCode`. Without either the API answers 403
 * `reauth_required`. The code is spent here, and the challenge returned is the only one `passkeyRegister`
 * then accepts.
 */
export const PasskeyRegisterOptionsInput = z.object({ code: z.string().regex(/^\d{6}$/, 'Enter the six-digit code from the email').optional() });
export const PasskeyAddCodeResponse = z.object({ expiresAt: z.string() });
export const PasskeyRegisterInput = z.object({ response: PasskeyRegistrationResponse, name: PasskeyName.optional() });
export const RenamePasskeyInput = z.object({ name: PasskeyName });
/**
 * Removing a passkey. While others remain, no code is needed. Removing the last one needs the six-digit
 * code emailed by `requestPasskeyRemovalCode`, so a stolen session alone cannot strip someone's passkeys.
 */
export const DeletePasskeyInput = z.object({ code: z.string().regex(/^\d{6}$/, 'Enter the six-digit code from the email').optional() });
export const PasskeyRemovalCodeResponse = z.object({ expiresAt: z.string() });
/** Sign-in options carry the challenge's id, sent back with the response. */
export const PasskeySignInOptions = z.object({ challengeId: z.uuid(), options: PasskeyRequestOptions });
export const PasskeySignInInput = z.object({ challengeId: z.uuid(), response: PasskeyAuthenticationResponse });
/**
 * The web domain's association files for the phone app (docs/adr/0100-passkeys-in-the-phone-app.md), served from
 * `/.well-known/` and built from the API's environment: universal links and shared web credentials (passkeys) on iOS,
 * App Links and `get_login_creds` (passkeys) on Android. Formats fixed by Apple and Google.
 */
export const AppleAppSiteAssociation = z.object({
    applinks: z.object({ details: z.array(z.object({ appIDs: z.array(z.string()), components: z.array(z.record(z.string(), z.string())) })) }),
    webcredentials: z.object({ apps: z.array(z.string()) }),
});
export const AssetLinks = z.array(z.object({
    relation: z.array(z.string()),
    target: z.object({ namespace: z.literal('android_app'), package_name: z.string(), sha256_cert_fingerprints: z.array(z.string()) }),
}));
// --- Two-factor sign-in (docs/adr/0033-two-factor-and-security-alerts.md) ----------------------------
const SixDigits = z.string().regex(/^\d{6}$/, 'Enter the 6-digit code from your authenticator app');
/** Recovery codes are ten letters and digits, shown as xxxxx-xxxxx; spaces, dashes and case are ignored. */
export const RecoveryCode = z.string().trim().min(10).max(20).regex(/^[A-Za-z0-9 -]+$/, 'Enter one of your recovery codes');
/** An authenticator code or a recovery code, for step-up on changing two-factor settings. */
export const StepUpFactorCode = z.union([SixDigits, RecoveryCode]);
export const VerifySecondFactorInput = z.discriminatedUnion('method', [
    z.object({ method: z.literal('totp'), code: SixDigits }),
    z.object({ method: z.literal('recovery_code'), code: RecoveryCode }),
    z.object({ method: z.literal('passkey'), challengeId: z.uuid(), response: PasskeyAuthenticationResponse }),
]);
export const SecondFactorPasskeyOptions = z.object({ challengeId: z.uuid(), options: PasskeyRequestOptions });
export const TwoFactorOverview = z.object({
    /** Whether the person has two-factor sign-in: an enabled authenticator or at least one passkey. */
    enabled: z.boolean(),
    totp: z.object({ enabled: z.boolean(), enabledAt: z.string().nullable() }),
    passkeys: z.number().int(),
    recoveryCodes: z.object({ remaining: z.number().int(), createdAt: z.string().nullable() }),
});
/** Setting up an authenticator needs a session signed in within 10 minutes, or `code` from requestTotpSetupCode. */
export const StartTotpSetupInput = z.object({ code: z.string().regex(/^\d{6}$/, 'Enter the six-digit code from the email').optional() });
export const TotpSetup = z.object({
    /** The base32 secret in groups of four, for typing into an authenticator by hand. */
    secret: z.string(),
    /** otpauth://totp/... for a QR code or a tap on the phone. */
    otpauthUri: z.string(),
    /** Valid until then; after that, start again. */
    expiresAt: z.string(),
});
export const TotpSetupCodeResponse = z.object({ expiresAt: z.string() });
export const ConfirmTotpSetupInput = z.object({ code: SixDigits });
/** Shown once; only hashes are kept. */
export const RecoveryCodesResponse = z.object({ recoveryCodes: z.array(z.string()).length(10) });
export const ConfirmTotpSetupResponse = RecoveryCodesResponse;
/**
 * Turning the authenticator off and regenerating recovery codes need step-up: `code` (an authenticator or
 * recovery code), or a second factor this device proved within the last 5 minutes. Otherwise 403
 * `reauth_required`.
 */
export const TwoFactorStepUpInput = z.object({ code: StepUpFactorCode.optional() });
export const SignOutEverywhereInput = z.object({ includeThisDevice: z.boolean().default(false) });
//# sourceMappingURL=auth.js.map